import { Link } from "react-router-dom";
import { MapPin, Building2, Flag, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { resolveCoachPhoto } from "@/lib/photoFallback";

/**
 * V9.21 — Sprint UX Simplification :
 * Refonte de la carte Coach pour aligner sa qualité visuelle sur `AthleteCard`.
 *
 * Attendus :
 *  - Photo / avatar propre (jamais un grand rectangle vide).
 *  - Nom lisible (2 lignes autorisées en mobile).
 *  - Discipline(s), club éventuel, fédération éventuelle.
 *  - Fonction / spécialité optionnelle si disponible dans le payload.
 *  - Redirige vers /coachs/:slug (ou /athletes/:slug si `kind === "athlete_as_coach"`).
 *
 * V11.11 — On utilise le fallback silhouette coach/encadrant AITO.PF dès qu'il
 *          n'y a pas de photo réelle (plus jamais d'initiales).
 */
export function CoachCard({ coach }) {
  if (!coach) return null;
  const isAthlete = coach.kind === "athlete_as_coach";
  // V13.17 — Depuis l'annuaire Coach on ouvre directement le volet Coach.
  const href = isAthlete
    ? `/athletes/${coach.slug || coach.id}?tab=coach`
    : `/coachs/${coach.slug || coach.id}`;

  const photoUrl = resolveCoachPhoto(coach);
  const hasRealPhoto = !!(coach.photo_url && String(coach.photo_url).trim());

  const disciplines = coach.disciplines || [];
  const disciplineLabel = coach.main_discipline || disciplines.slice(0, 2).join(" · ");
  const specialty = coach.specialty || coach.function || coach.role_title || null;

  return (
    <Link to={href} data-testid="coach-card" className="group block">
      <Card className="overflow-hidden aito-card-hover h-full">
        <div className="relative aspect-[4/3] bg-[hsl(var(--aito-black))]">
          {/* Photo réelle → full cover ; sinon silhouette officielle centrée sur fond dégradé */}
          {hasRealPhoto ? (
            <img
              src={photoUrl}
              alt={coach.full_name}
              className="absolute inset-0 h-full w-full object-cover opacity-95"
              onError={(e) => { e.currentTarget.src = "/branding/coach-default.png"; }}
            />
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--aito-gold))]/20 via-[hsl(var(--aito-black))]/60 to-[hsl(var(--aito-black))]" />
              <img
                src={photoUrl}
                alt={coach.full_name}
                className="absolute inset-0 h-full w-full object-contain p-4 opacity-95"
                loading="lazy"
              />
            </>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />

          {/* Badge "Coach" / spécialité en haut à gauche */}
          <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium bg-[hsl(var(--aito-lagoon))] text-black">
              <ShieldCheck className="h-3 w-3" /> Coach
            </span>
            {isAthlete && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-medium bg-[hsl(var(--aito-gold))]/90 text-black border border-black/10" title="Cette fiche est aussi une fiche athlète — cliquez pour la découvrir">
                Aussi athlète
              </span>
            )}
            {specialty && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium bg-black/60 text-white border border-white/20">
                {specialty}
              </span>
            )}
          </div>

          {/* Bloc identité en bas */}
          <div className="absolute inset-x-0 bottom-0 p-4 flex items-end gap-3 text-white">
            <div className="h-12 w-12 rounded-full overflow-hidden bg-white border-2 border-white/40 shrink-0">
              <img
                src={photoUrl}
                alt={coach.full_name}
                className="h-full w-full object-cover"
                onError={(e) => { e.currentTarget.src = "/branding/coach-default.png"; }}
              />
            </div>
            <div className="min-w-0">
              <div className="font-display font-semibold text-base leading-tight line-clamp-2 sm:line-clamp-1 sm:truncate">
                {coach.full_name}
              </div>
              {disciplineLabel && (
                <div className="text-xs text-white/80 mt-0.5 truncate">{disciplineLabel}</div>
              )}
            </div>
          </div>
        </div>

        <div className="p-3 space-y-1 text-xs">
          {coach.club && (
            <div className="flex items-center gap-1 text-muted-foreground min-w-0">
              <Building2 className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{coach.club}</span>
            </div>
          )}
          {coach.federation && (
            <div className="flex items-center gap-1 text-muted-foreground min-w-0">
              <Flag className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{coach.federation}</span>
            </div>
          )}
          {!coach.club && !coach.federation && (coach.commune || coach.island) && (
            <div className="flex items-center gap-1 text-muted-foreground min-w-0">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{coach.commune || coach.island}</span>
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
}
